// LeadProfileCard — the front-and-center profile panel on the Lead Detail page.
//
// Agents reported that opening a lead from the Leads list (which shows every
// profile field inline per row) dropped them onto a detail view where that same
// data was buried behind tabs. This card surfaces the full profile above the
// tabs so an agent on a live call can read — and edit — every field without
// navigating away.
//
// Every value is click-to-edit: clicking a field turns it into an input that
// saves with PUT /api/leads/:id (the same contract the Leads list inline edits
// use), so the card stays in sync with the list. Money fields are coerced to
// numbers on save. Unset fields render a muted "Not set" rather than being
// hidden, so agents can see what's missing and fill it in during the call.

import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Cake, Clock, Flame } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { apiPut } from '../lib/api.js';
import { fmtPhone, fmtMoney, fmtDate, fmtFromNow } from '../lib/format.js';
import { formatCustomFields } from '../lib/leadCustomFields.js';
import SourceAttribution from './SourceAttribution.jsx';
import { resolveLeadCost } from '../lib/leadCost.js';

// Money fields are stored as numbers; everything else is saved as-is.
const MONEY_FIELDS = new Set(['mortgageAmount', 'saleAmount', 'commission', 'cost', 'annualPremium', 'quote']);

// Compute current age from a birthday (ISO string or Date). Returns null when
// the birthday is missing or unparseable.
function ageFromBirthday(birthday) {
  if (!birthday) return null;
  const d = new Date(birthday);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 130) return null;
  return age;
}

// Lead's local wall-clock time for the agent to gauge "is it OK to call".
function localTimeFor(tz) {
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: tz || 'America/Denver',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

// ── Editable field — click the value to edit, Enter saves, Esc cancels ───────
// Mirrors the Leads-list InlineEditField contract (PUT /leads/:id { field })
// so edits made here show up on the list and vice-versa.
function EditableValue({ leadId, field, value, display, placeholder = 'Not set', editable = true, onSaved }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);

  const hasValue = value !== '' && value !== null && value !== undefined;
  const shown = display ?? (hasValue ? String(value) : '');

  async function save() {
    if (String(draft) === String(value ?? '')) { setEditing(false); return; }
    try {
      let payloadVal = draft;
      if (MONEY_FIELDS.has(field)) {
        const num = Number(String(draft).replace(/[^0-9.\-]/g, ''));
        payloadVal = Number.isFinite(num) ? num : 0;
      }
      await apiPut(`/leads/${leadId}`, { [field]: payloadVal });
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      setEditing(false);
      onSaved?.();
    } catch {
      toast.error('Save failed');
    }
  }

  if (editing && editable) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        className="w-full bg-civix-navy/60 border border-civix-purple/60 rounded-sm px-1.5 py-0.5 text-sm text-white"
      />
    );
  }

  if (!editable) {
    return <span className={hasValue ? 'text-white/90' : 'text-white/40 italic'}>{shown || placeholder}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to edit"
      className={`text-left hover:text-civix-cyan transition truncate ${hasValue ? 'text-white/90' : 'text-white/40 italic'}`}
    >
      {shown || placeholder}
    </button>
  );
}

// A single label/value cell in the grid.
function Field({ label, children, icon: Icon }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40 mb-0.5">
        {Icon && <Icon className="w-3 h-3 text-civix-purple/60" />}
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// Read-only cell for derived/non-editable values (age, local time, received-on).
function StaticField({ label, value, icon }) {
  const has = value !== '' && value !== null && value !== undefined;
  return (
    <Field label={label} icon={icon}>
      <span className={has ? 'text-white/90' : 'text-white/40 italic'}>{has ? value : 'Not set'}</span>
    </Field>
  );
}

// ── Loading skeleton — shimmer placeholders so agents never see a wall of
// "Not set" before the lead has actually loaded. ───────────────────────────
export function LeadProfileCardSkeleton() {
  return (
    <div className="panel-light p-4 mt-4 animate-pulse" data-testid="lead-profile-skeleton">
      <div className="h-3 w-28 bg-white/10 rounded-sm mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i}>
            <div className="h-2.5 w-20 bg-white/10 rounded-sm mb-2" />
            <div className="h-3.5 w-32 bg-white/10 rounded-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LeadProfileCard({ lead, onChange }) {
  if (!lead) return <LeadProfileCardSkeleton />;

  const leadId = lead._id;
  const refresh = () => onChange?.();
  const age = ageFromBirthday(lead.birthday);
  const localTime = localTimeFor(lead.timezone);
  const tier = lead.tier || lead.coverageTier || lead.customFields?.vendorTier;
  const vendorName = lead.vendor?.name || lead.leadVendorName || lead.leadType || '';
  const dispositionLabel = lead.disposition?.label || lead.disposition?.name || lead.status || '';
  const dispoCount = Array.isArray(lead.dispositionHistory) ? lead.dispositionHistory.length : 0;
  const lastTouched = lead.lastTouchedAt || lead.lastContactedAt || lead.updatedAt;
  const utmSource = lead.attribution?.utmSource || lead.customFields?.utmSource || '';

  const customFields = lead.customFields
    ? (lead.customFields instanceof Map ? Object.fromEntries(lead.customFields) : lead.customFields)
    : {};
  const extraFields = formatCustomFields(customFields, { productInterest: lead.productInterest });

  return (
    <div className="panel-light p-4 mt-4" data-testid="lead-profile-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label">Lead Profile</div>
        {dispositionLabel && <span className="pill-purple text-[10px]">{dispositionLabel}</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
        {/* Identity */}
        <Field label="Full name">
          <span className="text-white/90 font-semibold">{`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Not set'}</span>
        </Field>

        <Field label="Phone" icon={Phone}>
          {lead.phone
            ? <a href={`tel:${lead.phone}`} className="text-white/90 hover:text-civix-cyan transition">{fmtPhone(lead.phone)}</a>
            : <span className="text-white/40 italic">Not set</span>}
        </Field>

        <Field label="Email" icon={Mail}>
          {lead.email
            ? <a href={`mailto:${lead.email}`} className="text-white/90 hover:text-civix-cyan transition truncate inline-block max-w-full">{lead.email}</a>
            : <span className="text-white/40 italic">Not set</span>}
        </Field>

        <Field label="State / location" icon={MapPin}>
          <EditableValue leadId={leadId} field="state" value={lead.state || ''} onSaved={refresh} />
        </Field>

        <StaticField label="Birthday" icon={Cake} value={lead.birthday ? fmtDate(lead.birthday).split(' ').slice(0, 3).join(' ') : ''} />
        <StaticField label="Age" value={age != null ? `${age}` : ''} />
        <StaticField label="Local time" icon={Clock} value={localTime} />

        {/* Lead criteria */}
        <Field label="Mortgage amount">
          <EditableValue leadId={leadId} field="mortgageAmount" value={lead.mortgageAmount || ''}
            display={lead.mortgageAmount ? fmtMoney(Number(lead.mortgageAmount)) : ''} onSaved={refresh} />
        </Field>
        <Field label="Age range">
          <EditableValue leadId={leadId} field="ageRange" value={lead.ageRange || ''} onSaved={refresh} />
        </Field>
        <Field label="Beneficiary">
          <EditableValue leadId={leadId} field="beneficiary" value={lead.beneficiary || ''} onSaved={refresh} />
        </Field>
        <Field label="Protection goal">
          <EditableValue leadId={leadId} field="protectionGoal" value={lead.protectionGoal || ''} onSaved={refresh} />
        </Field>
        <Field label="Smoker">
          <EditableValue leadId={leadId} field="smoker" value={lead.smoker || ''} onSaved={refresh} />
        </Field>

        {/* Source */}
        <StaticField label="Coverage tier" value={tier || ''} />
        <StaticField label="Vendor source" value={vendorName || ''} />
        <StaticField label="Campaign / UTM source" value={utmSource || ''} />

        {/* Money */}
        <Field label="Sale amount">
          <EditableValue leadId={leadId} field="saleAmount" value={lead.saleAmount || ''}
            display={lead.saleAmount ? fmtMoney(Number(lead.saleAmount)) : ''} onSaved={refresh} />
        </Field>
        <Field label="Commission">
          <EditableValue leadId={leadId} field="commission" value={lead.commission || ''}
            display={lead.commission ? fmtMoney(Number(lead.commission)) : ''} onSaved={refresh} />
        </Field>
        <Field label="Cost for lead">
          <EditableValue leadId={leadId} field="cost" value={resolveLeadCost(lead) ?? ''}
            display={resolveLeadCost(lead) ? fmtMoney(resolveLeadCost(lead)) : ''} onSaved={refresh} />
        </Field>

        {/* Status */}
        <StaticField label="Received on" value={lead.createdAt ? fmtDate(lead.createdAt) : ''} />
        <Field label="Quote status">
          <EditableValue leadId={leadId} field="quote" value={lead.quote || ''} onSaved={refresh} />
        </Field>
        <StaticField label="Current disposition" value={dispositionLabel || ''} />
        <StaticField label="Last touched" value={lastTouched ? `${fmtFromNow(lastTouched)} · ${dispoCount} change${dispoCount === 1 ? '' : 's'}` : ''} />

        <Field label="Temperature" icon={Flame}>
          <span className={`pill ${lead.priority === 'HOT' ? 'pill-hot' : lead.priority === 'WARM' ? 'pill-warm' : 'pill-cold'}`}>
            {lead.priority || 'COLD'}
          </span>
        </Field>

        {/* Any remaining lead-supplied custom fields */}
        {extraFields.map(({ key, label, value }) => (
          <StaticField key={key} label={label} value={value} />
        ))}
      </div>

      {/* Source attribution — kept visible directly under the grid (collapsible). */}
      <SourceAttribution lead={lead} />
    </div>
  );
}
